import { useState, useEffect } from 'react'
import { User } from '../types/auth'
import { supabase } from '../lib/supabaseClient'

interface UseTrialParams {
  user: User | null
  isTrial: boolean
  trialEndDate: string | null
  onTrialChange: (isActive: boolean, endDate: string | null) => void
}

interface UseTrialReturn {
  trialDaysLeft: number | null
  updateTrialInDatabase: (email: string, isActive: boolean, endDate: string | null) => Promise<void>
}

export function useTrial({
  user,
  isTrial,
  trialEndDate,
  onTrialChange
}: UseTrialParams): UseTrialReturn {
  const [trialDaysLeft, setTrialDaysLeft] = useState<number | null>(null)

  // Function to update trial status in database
  const updateTrialInDatabase = async (
    email: string,
    isActive: boolean,
    endDate: string | null = null
  ) => {
    // Guard against inconsistent state: active trial must have an end date
    if (isActive && !endDate) {
      return
    }

    try {
      await supabase
        .from('profiles')
        .update({
          is_trial: isActive,
          trial_end_date: isActive ? endDate : null
        })
        // Use exact match with lowercase email
        .eq('email', email.toLowerCase())
        .select()
    } catch (err) {
      // Silent error handling
    }
  }

  // Calculate days left in trial with real-time expiration handling
  useEffect(() => {
    let isMounted = true

    if (trialEndDate) {
      const checkTrialExpiration = async () => {
        const endDate = new Date(trialEndDate)

        // Guard against malformed trialEndDate values
        if (isNaN(endDate.getTime())) {
          onTrialChange(false, null)
          return
        }

        const now = new Date()

        if (!isMounted) return

        if (endDate > now) {
          const diffTime = endDate.getTime() - now.getTime()
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
          setTrialDaysLeft(diffDays)
        } else {
          if (!isMounted) return

          setTrialDaysLeft(0)

          // Trial expired - update both local and database
          if (isTrial) {
            onTrialChange(false, null)

            // Update database if user is logged in
            if (user?.email) {
              await updateTrialInDatabase(user.email, false, null)
            }
          }
        }
      }

      checkTrialExpiration()

      // Set up interval to check every minute for trial expiration
      const interval = setInterval(() => {
        checkTrialExpiration()
      }, 60000) // Check every minute

      return () => {
        clearInterval(interval)
        isMounted = false
      }
    } else {
      setTrialDaysLeft(null)
    }
  }, [trialEndDate, isTrial, user?.email, onTrialChange])

  return {
    trialDaysLeft,
    updateTrialInDatabase
  }
}
